import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Database, Globe, BarChart3, Users, Cpu, AlertCircle } from 'lucide-react';




const getConsumerIcon = (type) => {
  switch (type) {
    case 'service':
      return Cpu;
    case 'dashboard':
      return BarChart3;
    case 'ml_model':
      return Database;
    case 'api':
      return Globe;
    case 'report':
      return Users;
    default:
      return Database;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-50';
    case 'inactive':
      return 'text-gray-600 bg-gray-50';
    case 'error':
      return 'text-red-600 bg-red-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
};

const ConsumerNode = ({ consumer, level }) => {
  const [isExpanded, setIsExpanded] = useState(level < 2);
  const Icon = getConsumerIcon(consumer.type);
  const hasChildren = consumer.dependencies && consumer.dependencies.length > 0;

  return (
    <div className="select-none">
      <div 
        className={`flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer`}
        style={{ marginLeft: `${level * 20}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        {hasChildren ? (
          isExpanded ? (
            <ChevronDown className="   h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="   h-4 text-gray-400 flex-shrink-0" />
          )
        ) : (
          <div className="   h-4 flex-shrink-0" />
        )}
        
        <Icon className="   h-4 text-blue-600 flex-shrink-0" />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">{consumer.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(consumer.status)}`}>
              {consumer.status}
            </span>
            {consumer.status === 'error' && (
              <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
            )}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {consumer.type.replace('_', ' ')} • Last accessed: {consumer.lastAccessed}
          </div>
        </div>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="mt-1">
            {consumer && consumer.dependencies?.map((child) => (
            <ConsumerNode key={child.id} consumer={child} level={level + 1} />
            ))}
        </div>
        )}
    </div>
  );
};

export const ConsumerTreeView = ({ consumers }) => {
  // Handle both string arrays and object arrays
  const renderConsumer = (consumer, index) => {
    // If consumer is a string (just product name)
    if (typeof consumer === 'string') {
      return (
        <div key={index} className="flex items-center gap-2 rounded-lg hover:bg-gray-50 transition-colors">
          <Database className=" h-4 text-blue-600 flex-shrink-0" />
          <span className="font-medium text-gray-900">{consumer}</span>
        </div>
      );
    }
    
    // If consumer is an object with nested structure
    if (consumer.dependencies && consumer.dependencies.length > 0) {
      return <ConsumerNode key={consumer.id || index} consumer={consumer} level={0} />;
    }
    
    // If consumer is an object from API (with project_name, product_id, data_asset_id)
    return (
      <div key={consumer.data_asset_id || consumer.id || index} className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors">
        <Database className=" h-4 text-blue-600 flex-shrink-0" />
        <span className="font-medium text-gray-900">{consumer.project_name || consumer.name || 'Unknown'}</span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h4 className="font-medium text-gray-900">Consumer Dependencies</h4>
      </div>
      
      <div className="p-2 max-h-[450px] overflow-y-auto">
        {consumers && consumers.length > 0 ? (
          <div className="space-y-1">
            {consumers.map((consumer, index) => renderConsumer(consumer, index))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Database className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No consumers found</p>
          </div>
        )}
      </div>
    </div>
  );
};